import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from "@angular/core";
import { formatUtcDate } from "../../shared/date-utils";
import { AiSettingsService } from "../../services/ai-settings.service";
import { AiAssessmentService, AssessmentJob, AssessmentJobHistoryPaged } from "../../services/ai-assessment.service";
import { AiAssessmentAdminService } from '../../services/ai-assessment-admin.service';
import { HttpErrorFormatter } from "../../services/http-error.formatter";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatDialog } from "@angular/material/dialog";
import { JsonViewDialogComponent } from "../../shared/json-view-dialog/json-view-dialog.component";
import { 
  AiConnection, 
  AiAssessmentSettings,
  AiAssessmentSettingsDetail,
  AiFeature 
} from "../../models/chat.models";
import { SpinnerComponent } from "../../shared/spinner/spinner.component";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MaterialModule } from "../../material.module";
import { LeftMenuComponent } from "../../menu/left-menu/left-menu.component";
import { UtcDatePipe } from "../../pipes/utc-date.pipe";
import { forkJoin, of, interval, Subscription } from "rxjs";
import { catchError, startWith, switchMap } from "rxjs/operators";
import { AiConfigSkeletonComponent } from "./ai-config-skeleton/ai-config-skeleton.component";
import { AiConfigJobHistorySkeletonComponent } from "./ai-config-job-history-skeleton/ai-config-job-history-skeleton.component";
import { BulkCodeValidationComponent } from "../ai-assessment/bulk-code-validation/bulk-code-validation.component";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";

@Component({
  selector: "modernization-ai-config",
  templateUrl: "./ai-config.component.html",
  styleUrls: ['./ai-config.component.scss'],
    imports: [CommonModule, FormsModule, MaterialModule, RouterLink, LeftMenuComponent, SpinnerComponent, UtcDatePipe, AiConfigSkeletonComponent, AiConfigJobHistorySkeletonComponent, BulkCodeValidationComponent]
})
export class AIConfigComponent implements OnInit, OnDestroy {
  // Tab state
  selectedTabIndex = 0;
  
  // Connections (loaded for dropdowns)
  connections: AiConnection[] = [];
  // Assessment Settings
  assessmentSettings: AiAssessmentSettingsDetail | null = null;
  editingAssessmentSettings: AiAssessmentSettings | null = null;
  assessmentVersions: any[] = [];
  showVersionHistory = false;
  
  // Job History
  jobHistory: AssessmentJob[] = [];
  jobHistoryPage = 1;
  jobHistoryPageSize = 20;
  jobHistoryTotalCount = 0;
  jobHistoryTotalPages = 0;
  jobHistoryLoading = false;

  @ViewChild('jobHistoryPaginator') jobHistoryPaginator?: MatPaginator;
  @ViewChild('logConsoleEl') logConsoleEl?: ElementRef;

  // Live logs (moved from standalone admin page)
  logLines: string[] = [];
  lastLogId = 0;
  private logSubscription?: Subscription;

  jobHistoryColumns: string[] = ['id', 'type', 'repositoryUrl', 'branch', 'status', 'requestedBy', 'createdAt', 'duration', 'tokens', 'details'];
  
  // UI state
  loading = false;
  saving = false;
  error: string | null = null;
  successMessage: string | null = null;
  
  // Enum for template
  AiFeature = AiFeature;

  constructor(
    private aiSettingsService: AiSettingsService,
    private aiAssessmentService: AiAssessmentService,
    private aiAssessmentAdminService: AiAssessmentAdminService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab?.toLowerCase() === 'history' || tab?.toLowerCase() === 'job-history') {
      this.selectedTabIndex = 2;
    }

    this.loadAllData();

    if (this.selectedTabIndex === 2) {
      this.loadJobHistory();
      this.startLiveLogs();
    }
  }

  ngOnDestroy(): void {
    this.stopLiveLogs();
  }

  loadAllData(): void {
    this.loading = true;
    this.error = null;

    forkJoin({
      connections: this.aiSettingsService.getAllConnections().pipe(
        catchError((err) => {
          const formatted = HttpErrorFormatter.formatError(err);
          console.error('Error loading connections:', formatted);
          this.error = `Failed to load connections: ${formatted.message}`;
          if (formatted.isNetworkFailure) {
            this.error += ' (Network unreachable)';
          }
          if (formatted.detail) {
            console.error('Error detail:', formatted.detail);
          }
          if (formatted.traceId) {
            console.error('Trace ID:', formatted.traceId);
          }
          return of([] as AiConnection[]);
        })
      ),
      settings: this.aiSettingsService.getActiveAssessmentSettings().pipe(
        catchError((err) => {
          if (err.status !== 404) {
            const formatted = HttpErrorFormatter.formatError(err);
            console.error('Error loading assessment settings:', formatted);
            if (!this.error) {
              this.error = `Failed to load assessment settings: ${formatted.message}`;
            }
            if (formatted.detail) {
              console.error('Error detail:', formatted.detail);
            }
            if (formatted.traceId) {
              console.error('Trace ID:', formatted.traceId);
            }
          }
          return of(null);
        })
      )
    }).subscribe({
      next: ({ connections, settings }) => {
        this.connections = connections;
        this.assessmentSettings = settings;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });

    this.loadAssessmentVersions();
  }

  loadAssessmentVersions(): void {
    this.aiSettingsService.getAllAssessmentVersions().subscribe({
      next: (versions) => {
        this.assessmentVersions = versions;
      },
      error: (err) => {
        console.error('Error loading assessment versions:', err);
      }
    });
  }

  toggleVersionHistory(): void {
    this.showVersionHistory = !this.showVersionHistory;
    if (this.showVersionHistory && this.assessmentVersions.length === 0) {
      this.loadAssessmentVersions();
    }
  }

  activateVersion(versionId: number): void {
    if (!confirm('Are you sure you want to activate this version? This will make it the active version for all new assessments.')) {
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    this.aiSettingsService.activateAssessmentVersion(versionId).subscribe({
      next: () => {
        this.successMessage = 'Version activated successfully!';
        this.saving = false;
        this.loadAllData();
        setTimeout(() => this.successMessage = null, 5000);
      },
      error: (err) => {
        console.error('Error activating version:', err);
        this.error = 'Failed to activate version: ' + (err.error?.message || err.message);
        this.saving = false;
      }
    });
  }

  startEditAssessmentSettings(): void {
    this.editingAssessmentSettings = this.assessmentSettings ? {
      id: this.assessmentSettings.id,
      version: this.assessmentSettings.version,
      versionName: this.assessmentSettings.versionName,
      aiConnectionId: this.assessmentSettings.connection.id,
      isActive: true,
      keyVaultSecretNameForPat: this.assessmentSettings.keyVaultSecretNameForPat,
      azureDevOpsBaseUrl: this.assessmentSettings.azureDevOpsBaseUrl,
      azureDevOpsOrganization: this.assessmentSettings.azureDevOpsOrganization,
      azureDevOpsProject: this.assessmentSettings.azureDevOpsProject,
      azureDevOpsWorkItemTitlePrefix: this.assessmentSettings.azureDevOpsWorkItemTitlePrefix,
      azureDevOpsParentWorkItemId: this.assessmentSettings.azureDevOpsParentWorkItemId,
      pollingIntervalSeconds: this.assessmentSettings.pollingIntervalSeconds,
      maxFileSizeBytes: this.assessmentSettings.maxFileSizeBytes,
      maxContentCharsPerDimensionChunk: this.assessmentSettings.maxContentCharsPerDimensionChunk,
      cicdPrompt: this.assessmentSettings.cicdPrompt,
      authPrompt: this.assessmentSettings.authPrompt,
      codePrompt: this.assessmentSettings.codePrompt,
      dependenciesPrompt: this.assessmentSettings.dependenciesPrompt,
      hostingPrompt: this.assessmentSettings.hostingPrompt,
      apiPrompt: this.assessmentSettings.apiPrompt,
      containerPrompt: this.assessmentSettings.containerPrompt,
      testingPrompt: this.assessmentSettings.testingPrompt,
      productionPrompt: this.assessmentSettings.productionPrompt,
      databasePrompt: this.assessmentSettings.databasePrompt,
      masterPrompt: this.assessmentSettings.masterPrompt,
      additionalDetailsPrompt: this.assessmentSettings.additionalDetailsPrompt,
      lightweightCodeValidationPrompt: this.assessmentSettings.lightweightCodeValidationPrompt,
      createdAt: this.assessmentSettings.createdAt,
      updatedAt: this.assessmentSettings.updatedAt
    } : {
      id: 0,
      version: 0,
      versionName: null,
      aiConnectionId: this.connections[0]?.id || 0,
      isActive: true,
      keyVaultSecretNameForPat: 'AzureDevOpsPAT',
      azureDevOpsBaseUrl: 'https://dev.azure.com/yourorg',
      azureDevOpsOrganization: '',
      azureDevOpsProject: '',
      azureDevOpsWorkItemTitlePrefix: '',
      azureDevOpsParentWorkItemId: 0,
      pollingIntervalSeconds: 30,
      maxFileSizeBytes: 1048576,
      maxContentCharsPerDimensionChunk: 150000,
      cicdPrompt: '',
      authPrompt: '',
      codePrompt: '',
      dependenciesPrompt: '',
      hostingPrompt: '',
      apiPrompt: '',
      containerPrompt: '',
      testingPrompt: '',
      productionPrompt: '',
      databasePrompt: '',
      masterPrompt: '',
      additionalDetailsPrompt: '',
      lightweightCodeValidationPrompt: '',
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  cancelEditFeatureSettings(feature: AiFeature): void {
    this.editingAssessmentSettings = null;
  }

  saveFeatureSettings(feature: AiFeature): void {
    this.saveAssessmentSettings();
  }

  saveAssessmentSettings(): void {
    if (!this.editingAssessmentSettings) return;

    // Validation
    if (this.editingAssessmentSettings.aiConnectionId === 0) {
      this.error = 'Please select a connection';
      return;
    }

    this.saving = true;
    this.error = null;
    this.successMessage = null;

    this.aiSettingsService.saveAssessmentSettings(this.editingAssessmentSettings).subscribe({
      next: () => {
        this.successMessage = 'Assessment settings saved successfully!';
        this.saving = false;
        this.editingAssessmentSettings = null;
        this.loadAllData();
        setTimeout(() => this.successMessage = null, 5000);
      },
      error: (err) => {
        console.error('Error saving assessment settings:', err);
        this.error = err.error?.message || 'Failed to save assessment settings';
        this.saving = false;
      }
    });
  }

  getConnectionName(connectionId: number): string {
    return this.connections.find(c => c.id === connectionId)?.name || 'Unknown';
  }

  // ==================== Job History ====================

  loadJobHistory(): void {
    this.jobHistoryLoading = true;
    this.aiAssessmentService.getAllJobsPaged(this.jobHistoryPage, this.jobHistoryPageSize).subscribe({
      next: (response) => {
        this.jobHistory = response.jobs ?? [];
        this.jobHistoryTotalCount = response.totalCount ?? 0;
        this.jobHistoryTotalPages = response.totalPages ?? 0;
        this.jobHistoryLoading = false;
      },
      error: (err) => {
        console.error('Error loading job history:', err);
        this.error = 'Failed to load job history';
        this.jobHistoryLoading = false;
      }
    });
  }

  onJobHistoryPageChange(event: PageEvent): void {
    this.jobHistoryPage = event.pageIndex + 1;
    this.jobHistoryPageSize = event.pageSize;
    this.loadJobHistory();
  }

  onTabChange(event: any): void {
    // When switching to Job History tab (index 2), refresh the list
    if (event.index === 2) {
      this.loadJobHistory();
      this.startLiveLogs();
    } else {
      this.stopLiveLogs();
    }
  }

  private startLiveLogs(): void {
    this.stopLiveLogs();

    this.logSubscription = interval(4000)
      .pipe(
        startWith(0),
        switchMap(() => this.aiAssessmentAdminService.getLiveLogs(this.lastLogId))
      )
      .subscribe({
        next: (response) => {
          if (response.entries?.length) {
            const newLines = response.entries
              .map(e => this.formatLogEntry(e.timestamp, e.level, e.message));

            if (newLines.length) {
              this.logLines = [...this.logLines, ...newLines];
              this.lastLogId = response.latestId;
              setTimeout(() => this.scrollLogsToBottom(), 50);
            } else {
              this.lastLogId = response.latestId;
            }
          }
        },
        error: () => {
          // best-effort polling
        }
      });
  }

  private stopLiveLogs(): void {
    this.logSubscription?.unsubscribe();
    this.logSubscription = undefined;
  }

  private formatLogEntry(timestamp: string, level: string, message: string): string {
    const ts = new Date(timestamp).toLocaleTimeString();
    return `[${ts}] [${level}] ${message}`;
  }

  private scrollLogsToBottom(): void {
    if (this.logConsoleEl) {
      const el = this.logConsoleEl.nativeElement as HTMLElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  getJobStatusBadgeClass(status: string): string {
    switch (status) {
      case 'Completed': return 'badge-success';
      case 'Running': return 'badge-primary';
      case 'Failed': return 'badge-danger';
      case 'Pending': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  }

  getJobStatusText(status: string | number): string {
    if (typeof status === 'number') {
      const statusMap: { [key: number]: string } = { 0: 'Pending', 1: 'Running', 2: 'Completed', 3: 'Failed' };
      return statusMap[status] || 'Unknown';
    }
    return status || 'Unknown';
  }

  isJobRateLimited(job: AssessmentJob): boolean {
    return this.getJobStatusText(job.status) === 'Running'
      && !!job.rateLimitedUntil
      && new Date(job.rateLimitedUntil).getTime() > Date.now();
  }

  getJobTypeLabel(job: AssessmentJob): string {
    return job.jobType === 'LightweightCodeValidation'
      || job.jobType === '1'
      || (job.jobType as any) === 1
      ? 'Lightweight'
      : 'Full Code';
  }

  formatJobDuration(job: AssessmentJob): string {
    if (!job.startedAt) {
      return 'Not started';
    }
    const start = new Date(job.startedAt).getTime();
    const end = job.completedAt ? new Date(job.completedAt).getTime() : Date.now();
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);

    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }

  formatJobDate(date: string | null): string {
    if (!date) {
      return 'N/A';
    }
    return formatUtcDate(new Date(date), 'short');
  }

  formatTokenCount(tokens: number): string {
    if (!tokens || tokens === 0) {
      return '0';
    }
    if (tokens >= 1000000) {
      return (tokens / 1000000).toFixed(1) + 'M';
    }
    if (tokens >= 1000) {
      return (tokens / 1000).toFixed(1) + 'K';
    }
    return tokens.toString();
  }

  truncateUrl(url: string, maxLength: number = 50): string {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  }

  viewResultJson(job: AssessmentJob): void {
    // Fetch full job with result JSON
    this.aiAssessmentService.getJob(job.id).subscribe({
      next: (fullJob) => {
        const result = fullJob.lightweightResult ?? fullJob.result;
        const jsonStr = result
          ? JSON.stringify(result, null, 2)
          : 'No result data available';
        
        this.dialog.open(JsonViewDialogComponent, {
          width: '900px',
          maxHeight: '90vh',
          data: {
            title: `AI Assessment Result — Job #${job.id}`,
            json: jsonStr
          }
        });
      },
      error: (err) => {
        console.error('Error loading job result:', err);
        this.error = 'Failed to load job result';
      }
    });
  }

  viewJobDetails(job: AssessmentJob): void {
    this.router.navigate(['/admin/ai-assessment/job', job.id], {
      queryParams: { returnTo: 'ai-config', tab: 'history' }
    });
  }
}
